import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  CopyPlus,
  Layers,
  Loader2,
  // Aliased, and it has to be: an unaliased `Map` would shadow the global
  // constructor this file builds its plate lookup with.
  Map as MapIcon,
  RotateCcw,
  Search,
  X,
} from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import { planningAPI, truckGroupAPI } from "../../services/api";
import HandlerScopeFilter from "../../components/handler/HandlerScopeFilter";
import { useHandlerScope } from "../../hooks/useHandlerScope";
import { useHandlers } from "../../hooks/useHandlers";
import { handlerName as nameOfHandler } from "../../types/handler";
import type { TruckGroup } from "../../types/truckGroups";
import { useSocketEvent } from "../../hooks/useSocketEvent";
import { useConfirm } from "../../hooks/useConfirm";
import {
  ContainerPool,
  type PoolLineFilter,
  type PoolLoadFilter,
} from "../../components/planning/ContainerPool";
import { PlanningGrid } from "../../components/planning/PlanningGrid";
import { PlanningMegaMap } from "../../components/planning/PlanningMegaMap";
import {
  addCell,
  applyCards,
  cardCode,
  cellsOf,
  planMove,
  removeCell,
} from "../../utils/planningBoard";
import type {
  PlanningBlock,
  PlanningBoard,
  PlanningCard,
  PlanningCell,
  PlanningPickIntent,
  PlanningPickup,
  PlanningSlotRef,
} from "../../types/planning";

/**
 * ── The container planning board ─────────────────────────────────────────────
 *
 * Where a week's container work is dispatched: which truck takes which box, on
 * which day, in what order — and, separately, when the driver gets to see it.
 *
 * ── Why this and not the operations calendar ─────────────────────────────────
 *
 * The shipment gantt (pages/calendar) is still there and still serves shipments.
 * It draws bars on a time axis, which is the right shape for a booking that has
 * a start and an end, and the wrong one for container dispatch: four container
 * moves in a day are a *sequence*, not four durations, and a driver asks "what
 * is next" rather than "what is at 11:20". So this is a grid of ordered slots,
 * and nothing was bent to make one screen serve both.
 *
 * ── The two presses ──────────────────────────────────────────────────────────
 *
 * Placing a container in a slot is planning and says nothing to anybody.
 * "Send to truck" is the created → planned move and is what puts the box on a
 * screen in a cab. Keeping them apart is the entire reason the board can be used
 * to plan a fortnight ahead — otherwise dropping next Thursday's job into a slot
 * would hand it to a driver this morning.
 *
 * ── Picking up, rather than dragging ─────────────────────────────────────────
 *
 * A card is *picked up* into state and then placed, and dragging is an extra
 * affordance layered on top for people with a pointer. Drag-and-drop does not
 * exist on touch, and this screen is used on a tablet in an office as much as on
 * a desktop — so the interaction that works everywhere is the primary one rather
 * than the fallback. It also gives the board something a drag cannot: a visible
 * "you are holding this, here is how to put it down" state.
 */

type Mode = "week" | "day";
type Density = "cards" | "rows";
type Sort = "deadline" | "city" | "client";

/** Monday of the week `date` falls in, as `YYYY-MM-DD`. */
function weekStart(date: Date) {
  const at = new Date(date);
  const weekday = at.getDay();
  at.setDate(at.getDate() + (weekday === 0 ? -6 : 1 - weekday));
  return dayKey(at);
}

/** A local calendar day as `YYYY-MM-DD` — never via ISO, which shifts the day. */
function dayKey(date: Date) {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
}

function shiftDays(day: string, by: number) {
  const at = new Date(`${day}T00:00:00`);
  at.setDate(at.getDate() + by);
  return dayKey(at);
}

/**
 * How many waiting containers the board asks for.
 *
 * The pool is a queue worked from the front, not an archive, and a company with
 * two thousand unplaced boxes does not want all of them on the wire to plan one
 * week. The cap is only safe because the *search* runs server-side — a
 * dispatcher looking for one container reaches the whole pool regardless of what
 * the first page happened to contain.
 */
const POOL_PAGE_SIZE = 200;

const EMPTY_BOARD: PlanningBoard = {
  days: [],
  slotCount: 4,
  trucks: [],
  assignments: [],
  blocks: [],
  pool: [],
  poolTotal: 0,
};

export default function ContainerPlanningPage() {
  const { t, i18n } = useTranslation("calendar");
  const navigate = useNavigate();
  const { confirm, confirmDialog } = useConfirm();

  const [mode, setMode] = useState<Mode>("week");
  const [anchor, setAnchor] = useState(() => weekStart(new Date()));

  const [board, setBoard] = useState<PlanningBoard>(EMPTY_BOARD);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [picked, setPicked] = useState<PlanningPickup | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  /**
   * The container whose slots are being edited — a second mode beside holding.
   *
   * Kept as a snapshot rather than an id, because the dispatcher may page to
   * next week to give the job a slot there, and a container with no cells in
   * the new window is not among that board's assignments. The live copy wins
   * whenever the board has one (see `editing` below).
   */
  const [editingCard, setEditingCard] = useState<PlanningCard | null>(null);

  const [poolDensity, setPoolDensity] = useState<Density>("cards");
  const [poolSort, setPoolSort] = useState<Sort>("deadline");
  const [poolSearch, setPoolSearch] = useState("");
  /**
   * The term the *server* is searching for, a beat behind the box.
   *
   * Typing is not a request. Sending one per keystroke would refetch the whole
   * board — trucks, a week of placements, blocks and the pool — eight times for
   * an eight-character container number, and the answers would race each other
   * back. The local filter (in `ContainerPool`) keeps the list responsive during
   * the wait, so the debounce costs nothing visible.
   */
  const [poolQuery, setPoolQuery] = useState("");
  const [poolDay, setPoolDay] = useState<string | null>(null);
  const [poolLoad, setPoolLoad] = useState<PoolLoadFilter>("all");
  const [poolLine, setPoolLine] = useState<PoolLineFilter>(null);
  /**
   * Leave running containers out of the pool — a server-side view preference.
   *
   * The odd one out among the pool's filters, and deliberately so. Load status
   * and shipping line narrow the page the server already sent; this one changes
   * what the server counts as waiting, because the pool is capped and a company
   * that plans from the container's own form has a pile that is mostly running
   * work. Filtering that in the sidebar would filter a page already full of it.
   * See `poolHideStarted` in the board validator.
   *
   * Off by default: a dispatcher who has not asked for anything should see every
   * container that has no place in the week, which is what the pool is.
   */
  const [poolHideStarted, setPoolHideStarted] = useState(false);

  /**
   * Mine / Everyone / a person, remembered for the board on its own.
   *
   * It narrows the *waiting pool* and nothing else. The grid keeps every
   * placement whatever this says — see `inScope` below, and the grey-tile
   * branch in `PlanningSlot`, for the reason that is not an oversight.
   */
  const { scope: handlerScope, setScope: setHandlerScope, param: handlerParam } =
    useHandlerScope("planning");
  const { byId: handlerById } = useHandlers();
  /** The pool is a drawer below `lg`, where there is no room for a sidebar. */
  const [poolOpen, setPoolOpen] = useState(false);

  const [truckSearch, setTruckSearch] = useState("");
  /**
   * Truck groups for the toolbar filter. They ride on the vehicles module, so a
   * company without it gets a refusal here — then the list stays empty and the
   * filter is simply not shown.
   */
  const [truckGroups, setTruckGroups] = useState<TruckGroup[]>([]);
  const [truckGroupId, setTruckGroupId] = useState("");

  useEffect(() => {
    let alive = true;
    truckGroupAPI
      .list()
      .then((res) => {
        if (alive) setTruckGroups(res.data?.data?.groups ?? []);
      })
      .catch(() => {
        if (alive) setTruckGroups([]);
      });
    return () => {
      alive = false;
    };
  }, []);

  /** The chosen group's members, or null for all trucks. */
  const groupMembers = useMemo(() => {
    const group = truckGroups.find((g) => g._id === truckGroupId);
    return group ? new Set(group.vehicleIds) : null;
  }, [truckGroups, truckGroupId]);

  const [freeSlots, setFreeSlots] = useState<number | null>(null);

  /**
   * The whole window on one map, over the board.
   *
   * A dialog rather than a panel beside the grid, and rather than a route on
   * this page. The grid and the map are two readings of the same week — one by
   * truck and day, one by geography — and a dispatcher uses them one at a time:
   * look at where the work is, decide who takes what, come back and place it.
   * Splitting the screen would shrink both into uselessness on the tablet this
   * is used from, and a route would lose the board's state on the way there and
   * back.
   */
  const [mapOpen, setMapOpen] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setPoolQuery(poolSearch.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [poolSearch]);

  const { start, end } = useMemo(
    () =>
      mode === "week"
        ? { start: anchor, end: shiftDays(anchor, 6) }
        : { start: anchor, end: anchor },
    [mode, anchor],
  );

  /**
   * Only the most recent load of *each kind* may write to state.
   *
   * The board refetches on a socket signal, on navigation and on every write,
   * and those overlap constantly on a busy desk. Without a guard the slowest
   * response wins, which shows a board that is one change out of date and will
   * not correct itself until something else happens.
   *
   * Two counters rather than one, and that is not tidiness — one counter was a
   * bug that emptied the grid. A full board load and a pool-only load are not
   * competing answers to the same question: the first brings the trucks, the
   * placements and the blocks, and the second deliberately brings none of them.
   * Sharing a counter let a pool-only response arriving second cancel an
   * in-flight board load, so its merge landed on a board that had never received
   * its trucks — and the grid rendered "No trucks match your search" with an
   * empty search box and no error to explain it.
   *
   * Separated, each kind only ever supersedes its own kind, which is the only
   * thing staleness can mean here.
   */
  const boardSeq = useRef(0);
  const poolSeq = useRef(0);

  /**
   * The pool term currently on the wire, whichever fetch put it there.
   *
   * Replaces a "have we searched once yet" flag, which could not survive
   * StrictMode: refs outlive the simulated remount, so the flag read as already
   * set on the second pass and the mount fired a pool-only load it was written
   * to prevent. This asks the question that actually matters — is the term the
   * effect wants already being fetched — and answering it is idempotent, so
   * running the effect twice does the work once.
   */
  const requestedQuery = useRef<string | null>(null);

  /**
   * Everything about the pool that only the *server* can answer, as one key.
   *
   * The term and the hide-started toggle both change what comes back and change
   * nothing else on the board, so both want the same quiet pool-only refetch —
   * and `requestedQuery` has to compare them together or toggling while a search
   * is running would read as "already on the wire" and never refetch.
   *
   * The load and line filters are not in it, and must not be: those are applied
   * in the sidebar over what was already sent, so asking the server again for
   * them would be a request that changes nothing.
   */
  const poolKey = `${poolHideStarted ? "1" : "0"}|${poolQuery}|${handlerParam ?? ""}`;

  const load = useCallback(
    async (options: { quiet?: boolean; poolOnly?: boolean } = {}) => {
      const poolOnly = Boolean(options.poolOnly);
      const seqRef = poolOnly ? poolSeq : boardSeq;
      const seq = ++seqRef.current;
      // Recorded before the request goes out, so the effect below can see that
      // this term is already covered and not ask for it a second time.
      requestedQuery.current = poolKey;
      if (!options.quiet) setLoading(true);
      try {
        const res = await planningAPI.getBoard({
          start,
          end,
          poolLimit: POOL_PAGE_SIZE,
          // Omitted rather than sent empty: the validator allows "", but not
          // sending it at all keeps the common request identical every time and
          // therefore cacheable by anything in front of it.
          ...(poolQuery ? { poolSearch: poolQuery } : {}),
          ...(poolHideStarted ? { poolHideStarted: true } : {}),
          ...(handlerParam ? { handler: handlerParam } : {}),
          ...(poolOnly ? { poolOnly: true } : {}),
        });
        if (seq !== seqRef.current) return;

        /**
         * A pool-only answer is merged, never assigned.
         *
         * It carries no trucks, no placements and no blocks — deliberately, so
         * a search does not drag a week of the fleet back down the wire — so
         * replacing the board with it would blank the grid on every keystroke.
         */
        const next = res.data.data;
        setBoard((current) =>
          poolOnly
            ? { ...current, pool: next.pool, poolTotal: next.poolTotal }
            : (next as PlanningBoard),
        );
        setError(null);
      } catch (err) {
        if (seq !== seqRef.current) return;
        const message =
          (err as { response?: { data?: { message?: string } } }).response?.data
            ?.message ?? t("planning.errors.load");
        setError(message);
      } finally {
        if (seq === seqRef.current) setLoading(false);
      }
    },
    [start, end, poolQuery, poolHideStarted, handlerParam, poolKey, t],
  );

  /**
   * Two triggers, two different fetches.
   *
   * Moving to another week is a new board and shows a spinner. Typing in the
   * pool's search — or toggling "hide started", which the server answers the
   * same way — is not: it changes the pool and can change nothing else, so it
   * asks for the pool alone and does it quietly. Refetching everything on a
   * debounce timer tore the grid down and redrew it while the dispatcher was
   * looking at it, which is what this split is for.
   *
   * `load` is deliberately out of both dependency lists. It is rebuilt whenever
   * the search term changes, so depending on it would make the window effect
   * fire on every keystroke — reintroducing exactly the full reload being
   * removed here. Both effects read it through the closure of the render that
   * scheduled them, which already holds the current values.
   */
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, end]);

  useEffect(() => {
    /*
     * Skip when this pool request is already on the wire.
     *
     * On mount the window effect above has just requested the board with this
     * very term, so there is nothing to add — and under StrictMode, which runs
     * every effect twice, that has to stay true on the second pass as well. A
     * "first run" flag could not do it: the ref survives the simulated remount,
     * so the second pass sailed past the guard, fired a pool-only load, and its
     * merge landed on a board with no trucks in it.
     */
    if (requestedQuery.current === poolKey) return;
    void load({ quiet: true, poolOnly: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poolKey]);

  /**
   * Somebody else moved something.
   *
   * A quiet refetch rather than a patch: the signal deliberately carries no
   * payload (see `emitPlanningChanged`), and a board reassembled from the server
   * cannot drift the way an incrementally-patched copy can. Quiet, because a
   * spinner every time a colleague drags a card would make the screen unusable
   * on a shared desk.
   */
  const socketReload = useRef<number | null>(null);
  useSocketEvent("planning:changed", () => {
    /*
     * Coalesced, because one action can be several signals.
     *
     * A swap emits once but reorders two containers, and a dispatcher working
     * quickly emits several times a second — each one asking every open board
     * for the whole week. Off the critical path since writes now apply their own
     * answer, so a short delay costs nothing and turns a burst into one request.
     */
    if (socketReload.current) window.clearTimeout(socketReload.current);
    socketReload.current = window.setTimeout(() => {
      socketReload.current = null;
      void load({ quiet: true });
    }, 400);
  });

  useEffect(
    () => () => {
      if (socketReload.current) window.clearTimeout(socketReload.current);
    },
    [],
  );

  /**
   * Escape puts down whatever is being held — the universal "never mind".
   *
   * Except while the map is over the board, where Escape means "close this",
   * and the map has its own handler saying so. Both listen on the window, so
   * without this the one key would close the map *and* silently drop a card
   * the dispatcher had picked up before opening it — two undos for one press,
   * one of them invisible.
   */
  useEffect(() => {
    if (mapOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setPicked(null);
        setEditingCard(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mapOpen]);

  /**
   * Run one board write, then reload.
   *
   * Every write goes through here so the three things that must happen around
   * all of them happen once: the card is marked busy so it cannot be double
   * pressed, a refusal is shown as the sentence the server wrote rather than as
   * a generic failure, and the board is refetched either way — including after a
   * failure, because a 409 usually means the board on screen is already stale.
   */
  /**
   * The board as it stands, for rolling an optimistic change back.
   *
   * Assigned during render rather than in an effect so a rollback scheduled
   * before the next paint still sees the board the user was actually looking at.
   */
  const boardRef = useRef(board);
  boardRef.current = board;

  /**
   * Run one board write: show it immediately, then let the server confirm it.
   *
   * This used to fire the request and then refetch the entire board — trucks, a
   * week of placements, the blocks and the pool — before anything moved on
   * screen. Two sequential round trips to a remote cluster, and until both
   * finished the card sat exactly where it was. Pressing "Place here" and
   * watching nothing happen for a second is not a slow board; it is a board that
   * declines to answer.
   *
   * So the change is drawn first, from `optimistic`, and the request goes out
   * behind it. When the response lands, `settle` applies the cards the server
   * actually wrote — authoritative, and normally identical, so nothing moves a
   * second time. The refetch is gone: every write already answers with what it
   * changed, and asking for the whole week again to learn one card's new slot
   * was the expensive way to be told something we had just been told.
   *
   * A failure rolls the board back to the snapshot and *then* resyncs, because a
   * refusal usually means the board on screen was stale to begin with.
   */
  const run = useCallback(
    async (
      cardId: string | null,
      action: () => Promise<unknown>,
      options: {
        success?: string;
        optimistic?: (board: PlanningBoard) => PlanningBoard;
        settle?: (board: PlanningBoard, response: unknown) => PlanningBoard;
      } = {},
    ) => {
      const snapshot = boardRef.current;

      // Both before the await, so the card lands and the holding bar clears in
      // the same frame as the click.
      if (options.optimistic) setBoard(options.optimistic);
      setPicked(null);
      setBusyId(cardId);

      try {
        const response = await action();
        if (options.settle) setBoard((b) => options.settle!(b, response));
        if (options.success) toast.success(options.success);
      } catch (err) {
        setBoard(snapshot);
        const message =
          (err as { response?: { data?: { message?: string } } }).response?.data
            ?.message ?? t("planning.errors.generic");
        toast.error(message);
        void load({ quiet: true });
      } finally {
        setBusyId(null);
      }
    },
    [load, t],
  );

  /** The cards a write answered with. */
  const cardsFrom = (response: unknown): PlanningCard[] => {
    const data = (response as { data?: { data?: { cards?: PlanningCard[]; card?: PlanningCard } } })
      ?.data?.data;
    if (data?.cards) return data.cards;
    return data?.card ? [data.card] : [];
  };

  const place = useCallback(
    (slotRef: PlanningSlotRef) => {
      if (!picked) return;
      const card = picked.card;
      // Slots are one truck's week: moving to another truck frees the extras,
      // and the dispatcher is told rather than left to notice.
      const freed =
        card.vehicleId && card.vehicleId !== slotRef.vehicleId
          ? (card.extraSlots?.length ?? 0)
          : 0;
      void run(
        card.id,
        () =>
          planningAPI.assign({
            lineId: card.id,
            vehicleId: slotRef.vehicleId,
            date: slotRef.date,
            slot: slotRef.slot,
          }),
        {
          ...(freed ? { success: t("planning.toast.extrasFreed", { count: freed }) } : {}),
          optimistic: (b) => planMove(b, card, slotRef),
          settle: (b, res) => applyCards(b, cardsFrom(res)),
        },
      );
    },
    [picked, run, t],
  );

  const send = useCallback(
    (card: PlanningCard) =>
      void run(card.id, () => planningAPI.send(card.id), {
        success: t("planning.toast.sent", { container: cardCode(card) }),
        // The badge flips the instant it is pressed. `sentAt` is guessed as now,
        // which is what the server stamps a few milliseconds later anyway.
        optimistic: (b) =>
          applyCards(b, [
            { ...card, sent: true, sentAt: new Date().toISOString(), status: "planned" },
          ]),
        settle: (b, res) => applyCards(b, cardsFrom(res)),
      }),
    [run, t],
  );

  const unsend = useCallback(
    async (card: PlanningCard) => {
      /**
       * Confirmed, because the driver is already looking at it.
       *
       * The container is on a screen in a cab right now, and taking it back is
       * the one status move in the module that runs backwards. That is worth one
       * question — not because it is dangerous (nothing has been driven, or the
       * server would refuse it) but because the person who needs to know is not
       * in the room.
       */
      const ok = await confirm({
        title: t("planning.confirm.unsend.title"),
        description: t("planning.confirm.unsend.message", {
          container: cardCode(card),
        }),
        confirmLabel: t("planning.confirm.unsend.confirm"),
      });
      if (!ok) return;

      void run(card.id, () => planningAPI.unsend(card.id), {
        success: t("planning.toast.unsent", { container: cardCode(card) }),
        optimistic: (b) =>
          applyCards(b, [{ ...card, sent: false, sentAt: null, status: "created" }]),
        settle: (b, res) => applyCards(b, cardsFrom(res)),
      });
    },
    [confirm, run, t],
  );

  const unassign = useCallback(
    async (card: PlanningCard) => {
      // A released container leaves a driver's screen when it is unassigned, so
      // it is worth one question — the person who needs to know is not in the
      // room. It stays released, though: unassigning is about the truck, and
      // the cross beside "Sent to truck" is what takes the release back. An
      // unreleased container has reached nobody, so it just goes to the pool.
      if (card.sent) {
        const ok = await confirm({
          title: t("planning.confirm.unassign.title"),
          description: t("planning.confirm.unassign.message", {
            container: cardCode(card),
          }),
          confirmLabel: t("planning.confirm.unassign.confirm"),
        });
        if (!ok) return;
      }

      if (editingCard?.id === card.id) setEditingCard(null);
      void run(card.id, () => planningAPI.unassign(card.id), {
        success: t("planning.toast.unassigned", { container: cardCode(card) }),
        /**
         * Off the truck and out of its slots — and nothing else.
         *
         * `sent`, `sentAt` and `status` are carried over untouched, because the
         * endpoint leaves them untouched. They used to be cleared here, which
         * made the card flash back to "not sent" before the server's answer put
         * the badge back.
         */
        optimistic: (b) =>
          applyCards(b, [
            {
              ...card,
              vehicleId: null,
              planningDate: null,
              slot: null,
              extraSlots: [],
            },
          ]),
        settle: (b, res) => applyCards(b, cardsFrom(res)),
      });
    },
    [confirm, run, t, editingCard],
  );

  /** The container being edited, as the board currently has it. */
  const editing = useMemo(
    () =>
      editingCard
        ? (board.assignments.find((card) => card.id === editingCard.id) ?? editingCard)
        : null,
    [board.assignments, editingCard],
  );

  /*
   * Leave the mode when the container stops being on a truck — unassigned by a
   * colleague, or refused back into the pool. There are no slots left to edit.
   */
  useEffect(() => {
    if (editingCard && board.pool.some((card) => card.id === editingCard.id)) {
      setEditingCard(null);
    }
  }, [board.pool, editingCard]);

  const toggleEditSlots = useCallback((card: PlanningCard) => {
    setPicked(null);
    setEditingCard((current) => (current?.id === card.id ? null : card));
  }, []);

  const addSlot = useCallback(
    (slotRef: PlanningSlotRef) => {
      if (!editing?.vehicleId) return;
      // The grid only offers cells on the container's own truck; this is the
      // same rule again, so a stale click can never ask for another one.
      if (slotRef.vehicleId !== editing.vehicleId) return;
      const card = editing;
      const cell: PlanningCell = { date: slotRef.date, slot: slotRef.slot };
      void run(
        card.id,
        () =>
          planningAPI.addSlot({
            lineId: card.id,
            vehicleId: slotRef.vehicleId,
            date: slotRef.date,
            slot: slotRef.slot,
          }),
        {
          optimistic: (b) => addCell(b, card, cell),
          settle: (b, res) => applyCards(b, cardsFrom(res)),
        },
      );
    },
    [editing, run],
  );

  const removeSlot = useCallback(
    (card: PlanningCard, cell: PlanningCell) =>
      void run(
        card.id,
        () => planningAPI.removeSlot({ lineId: card.id, date: cell.date, slot: cell.slot }),
        {
          optimistic: (b) => removeCell(b, card, cell),
          settle: (b, res) => applyCards(b, cardsFrom(res)),
        },
      ),
    [run],
  );

  const blockSlot = useCallback(
    (slotRef: PlanningSlotRef, reason?: string) =>
      void run(null, () => planningAPI.blockSlot({ ...slotRef, reason }), {
        /*
         * Drawn with a placeholder id until the server sends the real one.
         * The id is only used by Release, and the settle below replaces the
         * whole row before anyone can press it.
         */
        optimistic: (b) => ({
          ...b,
          blocks: [
            ...b.blocks,
            {
              id: `pending:${slotRef.vehicleId}:${slotRef.date}:${slotRef.slot}`,
              vehicleId: slotRef.vehicleId,
              date: slotRef.date,
              slot: slotRef.slot,
              reason: reason?.trim() || null,
            },
          ],
        }),
        settle: (b, res) => {
          const block = (res as { data?: { data?: { block?: PlanningBlock } } })?.data?.data
            ?.block;
          if (!block) return b;
          return {
            ...b,
            blocks: [
              ...b.blocks.filter(
                (x) =>
                  x.id !== block.id &&
                  !(
                    x.vehicleId === block.vehicleId &&
                    x.date === block.date &&
                    x.slot === block.slot
                  ),
              ),
              block,
            ],
          };
        },
      }),
    [run],
  );

  const releaseSlot = useCallback(
    (block: PlanningBlock) =>
      void run(null, () => planningAPI.releaseSlot(block.id), {
        // Nothing to settle: the endpoint answers with an empty body, and the
        // row is already gone from the board.
        optimistic: (b) => ({
          ...b,
          blocks: b.blocks.filter((x) => x.id !== block.id),
        }),
      }),
    [run],
  );

  const openCard = useCallback(
    (card: PlanningCard) =>
      navigate(`/container-orders/${card.orderId}/container/${card.id}`),
    [navigate],
  );

  /**
   * The one card opened out to show where its container goes, or null.
   *
   * One at a time, deliberately. The board can hold hundreds of cards and each
   * open one holds a live map and a fetched container — the stops are not on
   * the board's payload, and should not be, because a week of the fleet is
   * hundreds of cards and the endpoint sends each the handful of fields the
   * card reads. So opening is the question, and asking it closes the last one.
   */
  const [mapOpenId, setMapOpenId] = useState<string | null>(null);

  const toggleCardMap = useCallback(
    (card: PlanningCard) =>
      setMapOpenId((current) => (current === card.id ? null : card.id)),
    [],
  );

  const pick = useCallback(
    (pickup: PlanningPickup, intent: PlanningPickIntent = "toggle") => {
      // Picking a card up to move it ends slot editing — one mode at a time.
      setEditingCard(null);
      setPicked((current) =>
        // Clicking the held card again puts it down, so there is never a held
        // state you cannot get out of. A drag starting only ever picks up —
        // toggling there would drop the card in the middle of the gesture.
        intent === "toggle" && current?.card.id === pickup.card.id
          ? null
          : pickup,
      );
    },
    [],
  );

  /**
   * Truck name by vehicle id — its display name, else its plate.
   *
   * Built off the board's own truck list, which is the whole fleet — so a pool
   * container assigned to a truck the toolbar has filtered out still resolves
   * its plate. Filtering the grid must not change what a card in the pool says
   * about itself.
   */
  const plates = useMemo(
    () => new Map(board.trucks.map((truck) => [truck.id, truck.name])),
    [board.trucks],
  );

  const gridDays = useMemo(
    () => (mode === "week" ? board.days : [start]),
    [mode, board.days, start],
  );

  const dayFill = useMemo(() => {
    const fill = new Map<string, number>();
    const bump = (vehicleId: string, date: string) => {
      const k = `${vehicleId}|${date}`;
      fill.set(k, (fill.get(k) ?? 0) + 1);
    };
    for (const card of board.assignments) {
      if (!card.vehicleId) continue;
      for (const cell of cellsOf(card)) bump(card.vehicleId, cell.date);
    }
    // A block still waiting on the server (see `blockSlot`) is not counted, so
    // the row stays put until the server confirms it rather than vanishing
    // under the dispatcher's cursor and coming back if it is refused.
    for (const block of board.blocks) {
      if (block.id.startsWith("pending:")) continue;
      bump(block.vehicleId, block.date);
    }
    return fill;
  }, [board.assignments, board.blocks]);

  /**
   * Is this placed container inside the handler filter?
   *
   * Used only to decide how a card is *drawn*. `board.assignments` and
   * `board.blocks` come back whole regardless, and `dayFill` above is computed
   * from both — which is the entire reason the grid is never filtered
   * server-side. A colleague's job collapses to a grey tile and its slot stays
   * counted as taken; hiding it would make the cell read as free to the
   * dispatcher and to the count alike, and the next container dropped there
   * would be the second one promised to a truck that can do one.
   */
  const cardInScope = useCallback(
    (card: PlanningCard) =>
      !handlerParam || (card.handler?.id ?? null) === handlerParam,
    [handlerParam],
  );

  /** A handler's name from the roster — cards carry an id and may carry no name. */
  const handlerLabelOf = useCallback(
    (id: string | null | undefined) =>
      id ? nameOfHandler(handlerById(id)) : "",
    [handlerById],
  );

  /** Free slots on one truck-day — never negative, whatever the data says. */
  const freeSlotsOn = useCallback(
    (vehicleId: string, date: string) =>
      Math.max(0, board.slotCount - (dayFill.get(`${vehicleId}|${date}`) ?? 0)),
    [dayFill, board.slotCount],
  );

  const visibleDays = useMemo(() => {
    const byTruck = new Map<string, string[]>();
    if (freeSlots === null) return byTruck;
    for (const truck of board.trucks) {
      byTruck.set(
        truck.id,
        gridDays.filter((day) => freeSlotsOn(truck.id, day) === freeSlots),
      );
    }
    return byTruck;
  }, [freeSlots, board.trucks, gridDays, freeSlotsOn]);

  /** The trucks actually drawn, after the filters in the toolbar. */
  const trucks = useMemo(() => {
    const term = truckSearch.trim().toLowerCase();
    return board.trucks.filter((truck) => {
      if (groupMembers && !groupMembers.has(truck.id)) return false;
      if (
        term &&
        !`${truck.name} ${truck.plate} ${truck.model ?? ""}`.toLowerCase().includes(term)
      )
        return false;
      if (freeSlots === null) return true;
      // Judged *in the window on screen*, day by day: a truck stays if at least
      // one of its days has exactly that many slots free.
      return (visibleDays.get(truck.id)?.length ?? 0) > 0;
    });
  }, [board.trucks, truckSearch, freeSlots, visibleDays, groupMembers]);

  const noTrucksMessage = useMemo(() => {
    if (freeSlots !== null)
      return t("planning.grid.noTrucksFree", { count: freeSlots });
    if (truckSearch.trim()) return t("planning.grid.noTrucks");
    if (groupMembers) return t("planning.grid.noTrucksInGroup");
    return t("planning.grid.noTrucks");
  }, [freeSlots, truckSearch, groupMembers, t]);

  const windowLabel = useMemo(() => {
    const format = (day: string, withYear = false) =>
      new Date(`${day}T00:00:00`).toLocaleDateString(i18n.language, {
        day: "numeric",
        month: "short",
        ...(withYear ? { year: "numeric" } : {}),
      });

    return mode === "week"
      ? `${format(start)} – ${format(end, true)}`
      : new Date(`${start}T00:00:00`).toLocaleDateString(i18n.language, {
          weekday: "long",
          day: "numeric",
          month: "long",
        });
  }, [mode, start, end, i18n.language]);

  const step = (direction: 1 | -1) =>
    setAnchor((current) => shiftDays(current, direction * (mode === "week" ? 7 : 1)));

  const goToday = () => {
    const today = new Date();
    setAnchor(mode === "week" ? weekStart(today) : dayKey(today));
  };

  const switchMode = (next: Mode) => {
    if (next === mode) return;

    setAnchor((current) => {
      // Day → week: the week containing whatever day is on screen. Unambiguous.
      if (next === "week") return weekStart(new Date(`${current}T00:00:00`));

      /**
       * Week → day: today, when today is in the week being looked at.
       *
       * This used to land on the week's Monday, on the reasoning that the
       * dispatcher was reading *that* week and should keep their place. In
       * practice it was wrong four days out of five: somebody looking at the
       * current week and pressing "Day" wants today's work, and being shown
       * Monday on a Thursday reads as a broken button rather than as a
       * considered default.
       *
       * Keeping their place still matters for any *other* week, where "today"
       * is not in view at all and jumping to it would silently throw away the
       * week they navigated to. So the rule is: today if today is on screen,
       * otherwise the first day of the week they chose.
       */
      const today = dayKey(new Date());
      const weekEnd = shiftDays(current, 6);
      return today >= current && today <= weekEnd ? today : current;
    });

    setMode(next);
  };

  const pool = (
    <ContainerPool
      pool={board.pool}
      poolTotal={board.poolTotal}
      days={board.days}
      plates={plates}
      // A full page back means there is very likely more behind it. Exact would
      // need a count query on every board render to tell a dispatcher something
      // the notice already says well enough.
      truncated={board.pool.length >= POOL_PAGE_SIZE}
      density={poolDensity}
      sort={poolSort}
      search={poolSearch}
      dayFilter={poolDay}
      loadFilter={poolLoad}
      lineFilter={poolLine}
      picked={picked}
      busyId={busyId}
      onDensity={setPoolDensity}
      onSort={setPoolSort}
      onSearch={setPoolSearch}
      onDayFilter={setPoolDay}
      onLoadFilter={setPoolLoad}
      onLineFilter={setPoolLine}
      handlerName={handlerLabelOf}
      onPick={(pickup, intent) => {
        pick(pickup, intent);
        // On a phone the pool is a full-height drawer over the board, so a card
        // picked up in it has to close the drawer or there is nowhere to put it.
        setPoolOpen(false);
      }}
      onOpen={openCard}
      mapOpenId={mapOpenId}
      onToggleMap={toggleCardMap}
      onUnsend={(card) => void unsend(card)}
      hideStarted={poolHideStarted}
      onHideStarted={setPoolHideStarted}
    />
  );

  return (
    <div className="flex h-[calc(100vh-4rem)] min-h-0 flex-col lg:flex-row">
      {/* ── The waiting pool ─────────────────────────────────────────────── */}
      <aside className="hidden w-[340px] shrink-0 border-r border-gray-200 lg:block">
        {pool}
      </aside>

      {poolOpen && (
        <div className="fixed inset-0 z-40 flex lg:hidden">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setPoolOpen(false)}
          />
          <div className="relative ml-auto flex h-full w-[min(92vw,380px)] flex-col shadow-xl">
            <button
              type="button"
              onClick={() => setPoolOpen(false)}
              className="absolute right-3 top-3 z-10 rounded-full bg-white/90 p-1.5 text-gray-500 shadow"
              aria-label={t("planning.pool.close")}
            >
              <X className="h-4 w-4" />
            </button>
            {pool}
          </div>
        </div>
      )}

      {/* ── The board ────────────────────────────────────────────────────── */}
      <main className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 bg-white px-3 py-2.5 lg:gap-3 lg:px-5">
          <div className="flex shrink-0 overflow-hidden rounded-lg border border-gray-200">
            {(["week", "day"] as const).map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => switchMode(value)}
                className={`px-3 py-1.5 text-[12.5px] font-semibold transition ${
                  mode === value
                    ? "bg-dark-900 text-white"
                    : "bg-white text-gray-500 hover:bg-gray-50"
                }`}
              >
                {t(`planning.mode.${value}`)}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => step(-1)}
              className="rounded-md border border-gray-200 p-1.5 text-gray-500 transition hover:bg-gray-50"
              aria-label={t("planning.nav.previous")}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={goToday}
              className="rounded-md border border-gray-200 px-2.5 py-1.5 text-[12px] font-semibold text-gray-600 transition hover:bg-gray-50"
            >
              {t("planning.nav.today")}
            </button>
            <button
              type="button"
              onClick={() => step(1)}
              className="rounded-md border border-gray-200 p-1.5 text-gray-500 transition hover:bg-gray-50"
              aria-label={t("planning.nav.next")}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          <span className="text-[13.5px] font-semibold text-dark-900">
            {windowLabel}
          </span>

          {/* ── Whose work ──────────────────────────────────────────────
              Beside the truck filters, because on this screen they are read
              together: which trucks, and whose boxes. It narrows the waiting
              list only — the grid keeps everybody's jobs, drawn small, so the
              occupancy a dispatcher plans against stays true. */}
          <HandlerScopeFilter
            scope={handlerScope}
            onChange={setHandlerScope}
            className="ml-auto"
          />

          {truckGroups.length > 0 && (
            <select
              value={truckGroupId}
              onChange={(event) => setTruckGroupId(event.target.value)}
              className="rounded-md border border-gray-200 bg-white px-2 py-1.5 text-[12px] text-gray-600 focus:border-secondary-500 focus:outline-none focus:ring-1 focus:ring-secondary-500"
            >
              <option value="">{t("planning.grid.allGroups")}</option>
              {truckGroups.map((group) => (
                <option key={group._id} value={group._id}>
                  {group.name} ({group.vehicleIds.length})
                </option>
              ))}
            </select>
          )}

          <div
            className="relative w-full sm:w-[200px]"
          >
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
            <input
              value={truckSearch}
              onChange={(event) => setTruckSearch(event.target.value)}
              placeholder={t("planning.grid.searchTruck")}
              className="w-full rounded-md border border-gray-200 py-1.5 pl-8 pr-2 text-[12.5px] focus:border-secondary-500 focus:outline-none focus:ring-1 focus:ring-secondary-500"
            />
          </div>

          <label className="flex items-center gap-1.5 whitespace-nowrap text-[12px] font-semibold text-gray-500">
            {t("planning.grid.availableTrucks")}
            <select
              value={freeSlots ?? ""}
              onChange={(event) =>
                setFreeSlots(
                  event.target.value === "" ? null : Number(event.target.value),
                )
              }
              title={t("planning.grid.freeSlotsHint")}
              className="rounded-md border border-gray-200 bg-white px-2 py-1.5 text-[12px] font-normal text-gray-600 focus:border-secondary-500 focus:outline-none focus:ring-1 focus:ring-secondary-500"
            >
              <option value="">{t("planning.grid.anyAvailability")}</option>
              {Array.from(
                { length: board.slotCount + 1 },
                (_, index) => board.slotCount - index,
              ).map((value) => (
                <option key={value} value={value}>
                  {t("planning.grid.freeSlots", { count: value })}
                </option>
              ))}
            </select>
          </label>

          <button
            type="button"
            onClick={() => setMapOpen(true)}
            className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-gray-200 px-2.5 py-1.5 text-[12px] font-semibold text-gray-600 transition hover:bg-gray-50"
          >
            <MapIcon className="h-3.5 w-3.5" />
            {t("planning.map.open")}
          </button>

          <button
            type="button"
            onClick={() => void load()}
            className="rounded-md border border-gray-200 p-1.5 text-gray-500 transition hover:bg-gray-50"
            aria-label={t("planning.nav.refresh")}
          >
            <RotateCcw className="h-4 w-4" />
          </button>

          {/* The pool's own trigger, only where the sidebar is not shown. */}
          <button
            type="button"
            onClick={() => setPoolOpen(true)}
            className="flex items-center gap-1.5 rounded-md border border-gray-200 px-2.5 py-1.5 text-[12px] font-semibold text-gray-600 transition hover:bg-gray-50 lg:hidden"
          >
            <Layers className="h-3.5 w-3.5" />
            {t("planning.pool.title")}
            <span className="rounded-full bg-secondary-100 px-1.5 text-[10.5px] font-bold text-secondary-700">
              {board.poolTotal}
            </span>
          </button>
        </div>

        {/*
         * The holding bar.
         *
         * A picked-up card is a mode, and an unlabelled mode is how a board
         * becomes confusing — you tap a slot ten minutes later and something you
         * had forgotten you were holding lands in it. So it says what is held
         * and how to put it down, on every screen size.
         */}
        {editing && !picked && (
          <div className="flex items-center gap-2 border-b border-secondary-200 bg-secondary-50 px-3 py-2 lg:px-5">
            <CopyPlus className="h-3.5 w-3.5 shrink-0 text-secondary-700" />
            <span className="text-[12px] text-secondary-900">
              {t("planning.editingSlots", {
                container: cardCode(editing),
                plate: (editing.vehicleId && plates.get(editing.vehicleId)) || "",
              })}
            </span>
            <button
              type="button"
              onClick={() => setEditingCard(null)}
              className="ml-auto flex items-center gap-1 rounded-md border border-secondary-300 bg-white px-2 py-1 text-[11px] font-semibold text-secondary-700"
            >
              <X className="h-3 w-3" />
              {t("planning.card.slotsDone")}
            </button>
          </div>
        )}

        {picked && (
          <div className="flex items-center gap-2 border-b border-secondary-200 bg-secondary-50 px-3 py-2 lg:px-5">
            <span className="text-[12px] text-secondary-900">
              {t("planning.holding", { container: cardCode(picked.card) })}
            </span>
            <button
              type="button"
              onClick={() => setPicked(null)}
              className="ml-auto flex items-center gap-1 rounded-md border border-secondary-300 bg-white px-2 py-1 text-[11px] font-semibold text-secondary-700"
            >
              <X className="h-3 w-3" />
              {t("planning.putDown")}
            </button>
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-auto bg-gray-50 p-2 lg:p-4">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-20 text-gray-500">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-[13px]">{t("planning.loading")}</span>
            </div>
          ) : error ? (
            <div className="py-20 text-center">
              <p className="text-[13px] text-red-600">{error}</p>
              <button
                type="button"
                onClick={() => void load()}
                className="mt-3 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-[12px] font-semibold text-gray-600"
              >
                {t("planning.nav.refresh")}
              </button>
            </div>
          ) : (
            <PlanningGrid
              mode={mode}
              days={gridDays}
              daysFor={
                freeSlots !== null
                  ? (truckId) => visibleDays.get(truckId) ?? []
                  : undefined
              }
              freeSlotsOn={freeSlotsOn}
              noTrucksMessage={noTrucksMessage}
              slotCount={board.slotCount}
              trucks={trucks}
              assignments={board.assignments}
              blocks={board.blocks}
              picked={picked}
              editing={editing}
              busyId={busyId}
              onPick={pick}
              onDrop={place}
              onSend={send}
              onUnsend={(card) => void unsend(card)}
              onUnassign={(card) => void unassign(card)}
              onOpen={openCard}
              mapOpenId={mapOpenId}
              onToggleMap={toggleCardMap}
              onBlock={blockSlot}
              onRelease={releaseSlot}
              onEditSlots={toggleEditSlots}
              onAddSlot={addSlot}
              onRemoveSlot={removeSlot}
              // Drawing only — `assignments` above is deliberately unfiltered.
              inScope={cardInScope}
              handlerName={handlerLabelOf}
            />
          )}
        </div>
      </main>

      {mapOpen && (
        <PlanningMegaMap
          // The same window the grid is drawing — the map windows the *placed*
          // half to it, and shows every waiting container regardless.
          start={start}
          end={end}
          /*
           * The board's own window controls, lent to the map.
           *
           * Shared state rather than a copy: navigating on the map navigates
           * the grid behind it, so closing the map leaves the dispatcher on the
           * week they moved to. Two windows to reconcile would be two chances
           * to plan against the wrong one.
           */
          mode={mode}
          onMode={switchMode}
          onStep={step}
          onToday={goToday}
          plates={plates}
          /*
           * Reaching a container the board has not got.
           *
           * The pool's search runs server-side and reaches the whole pile, so
           * putting the number into it is exactly what makes an off-page
           * container available to pick up. Closing the map is part of the
           * gesture: the dispatcher asked to go and get it, and the sidebar is
           * where it will arrive.
           */
          onFindInPool={(code) => {
            setPoolSearch(code);
            setPoolOpen(true);
            setMapOpen(false);
          }}
          onOpen={(container) =>
            navigate(
              `/container-orders/${container.orderId}/container/${container.id}`,
            )
          }
          onClose={() => setMapOpen(false)}
        />
      )}

      {confirmDialog}
    </div>
  );
}
